import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import PublicRoutes from './PublicRoutes';
import PrivateRoutes from './PrivateRoutes';
import { useAuthStore } from '@/stores/auth';
import { useAlertStore } from '@/stores/alert';
import usePermissionsComposable from '@/composables/permissions/checkPermissions';
import { usePermitsStore } from '@/stores/user/permissions/permissions';
import { useAgentStore } from '@/stores/agent';
import { updatePageTitle } from '@/config/configHelpers';

const MAINTENANCE_MODE = false;

/**
 * Carga dinámica de rutas personalizadas desde brand/ si existe
 */
async function loadBrandRoutes() {
    // Usar import.meta.glob con ruta relativa
    // Desde core/src/router/index.ts busca en ../../../brand/src/router/index.ts
    const modules = import.meta.glob('../../../brand/src/router/index.ts');
    
    console.log('[ROUTER] 🔍 Checking for brand routes...', Object.keys(modules));
    
    if (Object.keys(modules).length === 0) {
        console.log('[ROUTER] ℹ️  No brand routes found, using only core routes');
        return null;
    }
    
    try {
        const moduleLoader = modules[Object.keys(modules)[0]];
        console.log('[ROUTER] ✅ Found brand router, loading...');
        
        const brandModule = await moduleLoader() as any;
        
        return {
            privateRoutes: brandModule.brandPrivateRoutes || [],
            publicRoutes: brandModule.brandPublicRoutes || [],
            moduleRoutes: brandModule.brandModuleRoutes || {}
        };
    } catch (error: any) {
        console.warn('[ROUTER] ⚠️  Error loading brand routes:', error?.message || error);
        return null;
    }
}

/**
 * Merge inteligente de rutas: brand puede sobrescribir o agregar
 */
function mergeRoutes(
    coreRoutes: RouteRecordRaw[],
    brandRoutes: RouteRecordRaw[]
): RouteRecordRaw[] {
    if (!brandRoutes || brandRoutes.length === 0) {
        return coreRoutes;
    }

    const coreRoutesMap = new Map<string, RouteRecordRaw>();
    coreRoutes.forEach((route) => {
        if (route.name) {
            coreRoutesMap.set(String(route.name), route);
        }
    });

    const mergedRoutes = [...coreRoutes];

    brandRoutes.forEach((brandRoute) => {
        if (brandRoute.name && coreRoutesMap.has(String(brandRoute.name))) {
            // Sobrescribir ruta existente
            const index = mergedRoutes.findIndex((r) => r.name === brandRoute.name);
            if (index !== -1) {
                mergedRoutes[index] = brandRoute;
                console.log(`[ROUTER] 🔄 Overriding route: ${String(brandRoute.name)}`);
            }
        } else {
            // Agregar nueva ruta
            mergedRoutes.push(brandRoute);
            console.log(`[ROUTER] ➕ Adding new route: ${String(brandRoute.name) || "unnamed"}`);
        }
    });

    return mergedRoutes;
}

// Cargar rutas de brand de forma asíncrona
const brandRoutes = await loadBrandRoutes();

// Preparar rutas finales
let finalPublicRoutes = PublicRoutes;
let finalPrivateRoutes = PrivateRoutes;

if (brandRoutes) {
    console.log('[ROUTER] 🎨 Merging brand routes with core routes...');
    
    // Mergear rutas públicas
    if (brandRoutes.publicRoutes.length > 0) {
        const publicChildren = PublicRoutes.children || [];
        const mergedPublicChildren = mergeRoutes(publicChildren, brandRoutes.publicRoutes);
        finalPublicRoutes = {
            ...PublicRoutes,
            children: mergedPublicChildren,
        };
        console.log(`[ROUTER] ✅ Merged ${brandRoutes.publicRoutes.length} public brand route(s)`);
    }

    // Mergear rutas privadas
    if (brandRoutes.privateRoutes.length > 0) {
        const privateChildren = PrivateRoutes.children || [];
        const mergedPrivateChildren = mergeRoutes(privateChildren, brandRoutes.privateRoutes);
        finalPrivateRoutes = {
            ...PrivateRoutes,
            children: mergedPrivateChildren,
        };
        console.log(`[ROUTER] ✅ Merged ${brandRoutes.privateRoutes.length} private brand route(s)`);
    }
} else {
    console.log('[ROUTER] 🔧 Using core routes only (standalone mode)');
}

export const router = createRouter({
    history: createWebHistory(),
    routes: [finalPublicRoutes, finalPrivateRoutes]
});

router.beforeEach(async (to, _from, next) => {
    if (MAINTENANCE_MODE && to.name !== 'Maintenance') {
        return next({ name: 'Maintenance', replace: true });
    }

    // Con el modo mantenimiento apagado, la pantalla de mantenimiento no debe ser
    // accesible: quien entre a /auth/maintenance (marcador viejo, enlace compartido)
    // va al login en vez de ver un aviso falso.
    if (!MAINTENANCE_MODE && to.name === 'Maintenance') {
        return next({ name: 'Login', replace: true });
    }

    // redirect to login page if not logged in and trying to access a restricted page
    const publicPages = ['/auth/login'];
    const authRequired = !publicPages.includes(to.path);
    const auth: any = useAuthStore();
    const storePermits: any = usePermitsStore();
    const agentStore = useAgentStore();

    //if (to.path === '/auth/login' && auth.user) {
    //    return next('/private'); // Ajusta '/private' a tu ruta de dashboard real
    //}
    if (to.name === 'Login' && auth.user) {
        const hashFolio = to.params.hash_folio as string | undefined;
        if (!hashFolio) {
            return next('/private'); // ruta por defecto
        } else {
            // v1 cerrada: destino directo en Mis Folios v2 Detalle (antes
            // `/private/activity/detail/v2/${hashFolio}`, ruta v1 ya bloqueada).
            return next({ name: 'Mis Folios v2 Detalle', params: { hash: hashFolio } });
        }
    }

    if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (authRequired && !auth.user) {
            auth.returnUrl = to.fullPath;
            auth.returnName = to.name;
            return next('/auth/login');
        }

        // Si la ruta es solo un redirect (sin componente propio), omitir operaciones costosas.
        // El guard volverá a correr para la ruta destino del redirect.
        const lastRecord = to.matched[to.matched.length - 1];
        if (lastRecord?.redirect !== undefined) {
            return next();
        }

        // v1 cerrada (mapa v1→v2, `router/v1ToV2.ts`): toda ruta v1 que todavía
        // declara `component` para no romper imports/tests lleva un `beforeEnter`
        // que la redirige a su destino v2. Ese `beforeEnter` corre DESPUÉS de este
        // guard global, así que si la ruta exige `requiresSystemPermission`/
        // `requiresPermission` y el usuario no tiene ESE permiso v1 puntual
        // (p. ej. `my_pending_tickets`, `my_renovations`), el guard cortaba a
        // `NoPermissions` antes de que el `beforeEnter` pudiera redirigir —
        // bloqueando la entrada en vez de resolverla a v2 (QA 2026-10-15:
        // `/private/receipt/list` y `/private/renovation/list`). La ruta v1
        // cerrada ya no necesita su gate de permiso propio: el único requisito es
        // `requiresAuth` (ya resuelto arriba) y dejar correr el `beforeEnter`.
        if (lastRecord?.beforeEnter !== undefined) {
            return next();
        }

        // Estas cargas (permisos + info de agente) no deben TUMBAR la navegación:
        // si una lanza (error de red, 403 transitorio, storage estricto), el guard
        // rechazaba la promesa y la navegación quedaba a medias → rebote a login.
        // Se capturan para que la ruta se resuelva aunque un catálogo falle.
        if (auth.user) {
            try {
                await storePermits.getPermits();
            } catch (e) {
                console.error('[ROUTER] getPermits falló (no bloquea navegación):', e);
            }
        }

        try {
            await agentStore.getInfoAgent();
        } catch (e) {
            console.error('[ROUTER] getInfoAgent falló (no bloquea navegación):', e);
        }

        const userStore = useAuthStore();

        if (to.meta.disabled) {
            return next({ name: 'Mi Tablero v2' });
        }

        if (to.meta.requiresSystemPermission) {
            if (usePermissionsComposable(to.meta.requiresSystemPermission, storePermits.permissions, agentStore.isAgent)) {
                return next(); // Permite el acceso
            } else {
                const alertStore = useAlertStore();
                alertStore.addAlert({
                    message: 'No tienes permisos para esta sección.',
                    type: 'error',
                    duration: 3000
                });

                return next({ name: 'NoPermissions' });
            }
        } else if (to.meta.requiresPermission) {
            // Si la ruta requiere permisos
            const requiredPermissions = Array.isArray(to.meta.requiresPermission)
                ? to.meta.requiresPermission
                : [to.meta.requiresPermission];

            if (userStore.groups != null) {
                // Verificar si el usuario pertenece a al menos uno de los grupos requeridos
                const hasPermissionGroup = userStore.groups.some((group: { name: string }) => requiredPermissions.includes(group.name));

                /*   const hasPermissionSystem = userStore.groups.some((group: { name: string }) => requiredPermissions.includes(group.name));
                 ; */

                // console.log('hasPermissionGroup: ', hasPermissionGroup);

                if (hasPermissionGroup) {
                    return next(); // Permite el acceso
                } else {
                    const alertStore = useAlertStore();
                    alertStore.addAlert({
                        message: 'No tienes permisos para esta sección.',
                        type: 'error',
                        duration: 3000
                    });

                    return next({ name: 'NoPermissions' });
                }
            }
        }
    }

    next(); // Asegura que next se llame en todas las rutas
});

/**
 * Título de la pestaña por sección.
 *
 * Se hace en `afterEach` (no en el guard) porque el guard tiene varios `return
 * next()` tempranos y ahí el título solo se aplicaría a algunas navegaciones. El
 * `afterEach` corre tras cada navegación resuelta, que es el lugar idiomático
 * para `document.title`. El texto sale de `to.meta.title` combinado con
 * `getCompanyName()` vía el helper existente `updatePageTitle`; sin `meta.title`
 * cae al nombre de la empresa a secas (comportamiento previo).
 */
router.afterEach((to) => {
    const metaTitle = to.meta?.title;
    updatePageTitle(typeof metaTitle === 'string' && metaTitle ? metaTitle : undefined);
});
